import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RCS_CONVERSATION_STATUS_ENDED } from '@rsos/constants/rcsStatuses';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { useGetRcsConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/rcs/rcsApi';
import { useSelectedRcsConversation } from '@rsos/reduxDataStore/src/features/rcs/rcsSelectors';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { trackLiveRCSCardClick } from '../../CallChats/rcsTrackings';
import LiveChatCard from './LiveChatCard';

const CHAT_TAB = 'chat';

/**
 * Standard RCS chat card component that used in the ChatCard.js component.
 */
const RcsChatCard = () => {
  const dispatch = useDispatch();

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const rcsConversation = useSelectedRcsConversation(selectedCallerID);

  // get RCS conversation metadata for tracking
  const conversationId = useMemo(
    () => rcsConversation?.data?.conversation_id,
    [rcsConversation],
  );
  const sessionId = useMemo(
    () => rcsConversation?.data?.session_id,
    [rcsConversation],
  );

  const displayPhoneNumber =
    formatInternationalPhoneNumberHuman(selectedCallerID);

  const handleSetChatTab = useCallback(
    () => dispatch(setSelectedTab(CHAT_TAB)),
    [dispatch],
  );

  const handleLiveChatCardClick = useCallback(() => {
    handleSetChatTab();
    trackLiveRCSCardClick({
      conversationId,
      sessionId,
    });
  }, [handleSetChatTab, conversationId, sessionId]);

  const isRcsClosed = useMemo(
    () =>
      rcsConversation &&
      rcsConversation.data &&
      rcsConversation.data.conversationStatus === RCS_CONVERSATION_STATUS_ENDED,
    [rcsConversation],
  );

  useGetRcsConversationMetadataQuery(
    {
      orgName,
      callerID: selectedCallerID,
    },
    {
      skip: !selectedCallerID || !!conversationId,
    },
  );

  return (
    <div data-name="tf911-chat-card">
      <LiveChatCard
        callerID={displayPhoneNumber}
        onClick={handleLiveChatCardClick}
        isRcs
        isRcsClosed={isRcsClosed}
      />
    </div>
  );
};

export default RcsChatCard;
